import { useState, useEffect, createContext, useContext } from 'react';
import { apiService } from '../services/api';

const UserContext = createContext();

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      loadUserProfile();
    } else {
      setLoading(false);
    }
  }, []);

  // Retry mechanism for failed profile loads
  const retryLoadUserProfile = async (retries = 3) => {
    for (let i = 0; i < retries; i++) {
      try {
        await loadUserProfile();
        return; // Success, exit retry loop
      } catch (err) {
        console.warn(`Profile load attempt ${i + 1} failed:`, err.message);
        if (i === retries - 1) {
          // Last attempt failed, handle the error
          console.error('All retry attempts failed for user profile');
        } else {
          // Wait before retrying (exponential backoff)
          await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, i)));
        }
      }
    }
  };


  const loadUserProfile = async () => {
    try {
      setLoading(true);
      setError(null);
      const userData = await apiService.getUserProfile();
      setUser(userData);
      return userData;
    } catch (err) {
      console.error('Failed to load user profile:', err);
      setError(err.message);
      
      // Only remove token if it's specifically an authentication error
      // Check if the error is due to invalid token (401) or user not found (403)
      if (err.message && (
        err.message.includes('Invalid token') || 
        err.message.includes('Access denied') ||
        err.message.includes('User not found') ||
        err.message.includes('Account is deactivated') ||
        err.message.includes('locked out')
      )) {
        localStorage.removeItem('token');
        setUser(null);
      } else {
        // For other errors (network, server issues), keep the token but show error
        console.warn('Non-auth error occurred, keeping token:', err.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      // Call logout API to log the logout action
      await apiService.logout();
    } catch (error) {
      // Even if API call fails, we still want to clear local storage
      console.warn('Logout API call failed:', error);
    } finally {
      // Always clear local storage and user state
      localStorage.removeItem('token');
      setUser(null);
    }
  };

  const hasRole = (role) => {
    return user?.role === role;
  };

  const hasAnyRole = (roles) => {
    return roles.includes(user?.role);
  };

  const canAccessUserManagement = () => {
    return hasAnyRole(['superAdmin', 'companyAdmin']);
  };

  const canEditUser = (targetUser) => {
    if (!user) return false;
    
    // SuperAdmin can edit anyone
    if (user.role === 'superAdmin') return true;
    
    // CompanyAdmin can only edit users from their company
    if (user.role === 'companyAdmin') {
      return user.assetId === targetUser.assetId;
    }
    
    // Regular users cannot edit anyone
    return false;
  };

  const value = {
    user,
    loading,
    error,
    loadUserProfile,
    retryLoadUserProfile,
    logout,
    hasRole,
    hasAnyRole,
    canAccessUserManagement,
    canEditUser
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};
